import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Filter,
  Search,
  Calendar,
  Loader2,
  CheckCircle2,
  Download,
  X,
  Image as ImageIcon,
  PackagePlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, exportMySalesCsv } from "../../services/api";
import { getColourPresentation } from "../../utils/colourPresentation";
import { useNotification } from "../../hooks/useNotification";
import { useCurrencyFormatter } from "../../utils/formatCurrency";

const toLocalDateInputValue = (value = new Date()) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 10);
};

const getDateDaysAgoInputValue = (daysAgo = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return toLocalDateInputValue(date);
};

const getDateMonthsAgoInputValue = (monthsAgo = 1) => {
  const date = new Date();
  date.setMonth(date.getMonth() - monthsAgo);
  return toLocalDateInputValue(date);
};

const getNextDay = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return toLocalDateInputValue(d);
};

const getExportDateRangeFromPreset = (preset) => {
  const today = getDateDaysAgoInputValue(0);

  switch (preset) {
    case "last2":
      return {
        startDate: getDateDaysAgoInputValue(2),
        endDate: today,
      };
    case "last7":
      return {
        startDate: getDateDaysAgoInputValue(7),
        endDate: today,
      };
    case "lastMonth":
      return {
        startDate: getDateMonthsAgoInputValue(1),
        endDate: today,
      };
    case "today":
    default:
      return {
        startDate: today,
        endDate: today,
      };
  }
};

const createDefaultExportConfig = () => {
  const { startDate, endDate } = getExportDateRangeFromPreset("today");
  return {
    datePreset: "today",
    outletScope: "all",
    outletKey: "",
    startDate,
    endDate,
  };
};

const normalizeOutletLabel = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

function HistoryView() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { format: formatCurrency, currency } = useCurrencyFormatter();
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState("activity");
  const [activityFilterDate, setActivityFilterDate] = useState("");
  const [activityFilterType, setActivityFilterType] = useState("All");
  const [activitySearch, setActivitySearch] = useState("");
  const [visitFilterDate, setVisitFilterDate] = useState("");
  const [visitSearch, setVisitSearch] = useState("");
  const [visitFilterEventStatus, setVisitFilterEventStatus] = useState("All");

  const [sales, setSales] = useState([]);
  const [displayProofs, setDisplayProofs] = useState([]);
  const [stockRequests, setStockRequests] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportConfig, setExportConfig] = useState(() => createDefaultExportConfig());

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingHistory(true);

        const [salesResponse, displayProofResponse, stockRequestResponse] =
          await Promise.allSettled([
            api.get("/sales?limit=500&offset=0"),
            api.get("/display-proofs/me?limit=500&offset=0"),
            api.get("/stock/requests?limit=500&offset=0"),
          ]);

        if (salesResponse.status === "fulfilled") {
          setSales(salesResponse.value.data.success ? salesResponse.value.data.data : []);
        } else {
          console.error("Failed to fetch sales history:", salesResponse.reason);
          setSales([]);
        }

        if (displayProofResponse.status === "fulfilled") {
          setDisplayProofs(
            displayProofResponse.value.data.success
              ? displayProofResponse.value.data.data
              : [],
          );
        } else {
          console.error(
            "Failed to fetch display proof history:",
            displayProofResponse.reason,
          );
          setDisplayProofs([]);
        }

        if (stockRequestResponse.status === "fulfilled") {
          setStockRequests(
            stockRequestResponse.value.data.success
              ? stockRequestResponse.value.data.data
              : [],
          );
        } else {
          console.error(
            "Failed to fetch stock request history:",
            stockRequestResponse.reason,
          );
          setStockRequests([]);
        }
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
        setSales([]);
        setDisplayProofs([]);
        setStockRequests([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchInitialData();
  }, []);

  const fetchCheckIns = useCallback(async ({ startDate, endDate, limit = 500 } = {}) => {
    try {
      setLoadingHistory(true);
      const params = new URLSearchParams({ limit, offset: 0 });
      if (startDate && endDate) {
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      }
      const response = await api.get(`/checkins/history?${params}`);
      setCheckIns(response.data.success ? response.data.data : []);
    } catch (error) {
      console.error("Failed to fetch check-in history:", error);
      setCheckIns([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    const today = toLocalDateInputValue(new Date());
    if (visitFilterDate) {
      fetchCheckIns({ startDate: visitFilterDate, endDate: getNextDay(visitFilterDate) });
    } else {
      fetchCheckIns({ startDate: today, endDate: getNextDay(today), limit: 15 });
    }
  }, [visitFilterDate, fetchCheckIns]);

  const normalizeProofTypeCode = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

  const normalizeVisitStatusCode = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

  const isVisitsTab = activeHistoryTab === "visits";
  const activeSearchValue = isVisitsTab ? visitSearch : activitySearch;
  const activeSearchPlaceholder = isVisitsTab
    ? t("se.history.searchVisitsPlaceholder")
    : t("se.history.searchPlaceholder");

  const resetActiveFilters = () => {
    if (isVisitsTab) {
      setVisitFilterDate("");
      setVisitSearch("");
      setVisitFilterEventStatus("All");
      return;
    }

    setActivityFilterDate("");
    setActivityFilterType("All");
    setActivitySearch("");
  };

  const resolveExportFilename = (headers = {}, fallbackDate) => {
    const contentDisposition =
      headers["content-disposition"] || headers["Content-Disposition"] || "";

    const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FilenameMatch?.[1]) {
      try {
        return decodeURIComponent(utf8FilenameMatch[1]);
      } catch (_) {
        return utf8FilenameMatch[1];
      }
    }

    const quotedFilenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (quotedFilenameMatch?.[1]) {
      return quotedFilenameMatch[1];
    }

    return `my_sales_${fallbackDate}.csv`;
  };

  const getProofTypeLabel = (proofType) => {
    const code = normalizeProofTypeCode(proofType);
    switch (code) {
      case "common_display":
        return t("se.displayProof.types.commonDisplay");
      case "display_corner":
        return t("se.displayProof.types.displayCorner");
      case "promo_area":
        return t("se.displayProof.types.promoArea");
      case "others":
      case "other":
        return t("se.displayProof.types.others");
      case "station":
        return t("se.displayProof.types.station");
      case "shelf":
        return t("se.displayProof.types.shelf");
      default:
        return code
          ? code
              .split("_")
              .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
              .join(" ")
          : t("se.history.displayProofLabel");
    }
  };

  const saleHistoryRecords = sales.map((sale) => ({
    ...sale,
    __recordType: "sale",
    __recordKey: `sale-${sale.sale_id}`,
    __timestamp: sale.sale_date || sale.checkIn?.checked_in_at || null,
    total_amount: Number(sale.total_amount || 0),
  }));

  const exportOutletOptions = useMemo(() => {
    const uniqueOptions = new Map();

    saleHistoryRecords.forEach((sale) => {
      const outletId = Number.parseInt(
        sale?.outlet?.bp_outlet_id || sale?.outlet_id,
        10,
      );
      const outletName = normalizeOutletLabel(
        sale?.outlet?.bp_outlet_name || sale?.checkIn?.outlet_name || sale?.outlet_name,
      );

      if (Number.isInteger(outletId) && outletId > 0) {
        const key = `id:${outletId}`;
        if (!uniqueOptions.has(key)) {
          uniqueOptions.set(key, {
            key,
            label: outletName || `Outlet #${outletId}`,
            outletId,
            locationTerm: outletName || "",
          });
        }
        return;
      }

      if (!outletName) return;

      const key = `name:${outletName.toLowerCase()}`;
      if (!uniqueOptions.has(key)) {
        uniqueOptions.set(key, {
          key,
          label: outletName,
          outletId: null,
          locationTerm: outletName,
        });
      }
    });

    return Array.from(uniqueOptions.values()).sort((left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: "base" }),
    );
  }, [saleHistoryRecords]);

  const selectedExportOutlet = useMemo(
    () =>
      exportOutletOptions.find((option) => option.key === exportConfig.outletKey) ||
      null,
    [exportConfig.outletKey, exportOutletOptions],
  );

  const exportDatePresetOptions = useMemo(
    () => [
      {
        value: "today",
        label: t("se.history.exportModal.presets.today", {
          defaultValue: "Today",
        }),
      },
      {
        value: "last2",
        label: t("se.history.exportModal.presets.last2", {
          defaultValue: "Last 2 days",
        }),
      },
      {
        value: "last7",
        label: t("se.history.exportModal.presets.last7", {
          defaultValue: "Last 7 days",
        }),
      },
      {
        value: "lastMonth",
        label: t("se.history.exportModal.presets.lastMonth", {
          defaultValue: "Last 1 month",
        }),
      },
      {
        value: "custom",
        label: t("se.history.exportModal.presets.custom", {
          defaultValue: "Custom",
        }),
      },
    ],
    [t],
  );

  const closeExportModal = () => {
    if (exportingCsv) return;
    setShowExportModal(false);
  };

  const applyExportDatePreset = (preset) => {
    if (preset === "custom") {
      setExportConfig((previous) => ({
        ...previous,
        datePreset: "custom",
      }));
      return;
    }

    const { startDate, endDate } = getExportDateRangeFromPreset(preset);
    setExportConfig((previous) => ({
      ...previous,
      datePreset: preset,
      startDate,
      endDate,
    }));
  };

  const openExportModal = () => {
    if (
      activityFilterType === "display_proof" ||
      activityFilterType === "stock_request"
    ) {
      showError(
        t("se.history.exportSalesOnly", {
          defaultValue: "CSV export only supports sales and returns.",
        }),
      );
      return;
    }

    if (saleHistoryRecords.length === 0) {
      showError(
        t("se.history.noSalesToExport", {
          defaultValue: "No sales records available to export.",
        }),
      );
      return;
    }

    setExportConfig(createDefaultExportConfig());
    setShowExportModal(true);
  };

  const handleConfirmExport = async () => {
    if (
      activityFilterType === "display_proof" ||
      activityFilterType === "stock_request"
    ) {
      showError(
        t("se.history.exportSalesOnly", {
          defaultValue: "CSV export only supports sales and returns.",
        }),
      );
      return;
    }

    if (!exportConfig.startDate || !exportConfig.endDate) {
      showError(
        t("se.history.exportModal.validationDateRequired", {
          defaultValue: "Please select both start and end dates.",
        }),
      );
      return;
    }

    if (exportConfig.endDate < exportConfig.startDate) {
      showError(
        t("se.history.exportModal.validationDateRange", {
          defaultValue: "End date cannot be earlier than start date.",
        }),
      );
      return;
    }

    if (exportConfig.outletScope === "single" && !selectedExportOutlet) {
      showError(
        t("se.history.exportModal.validationSelectOutlet", {
          defaultValue: "Please select an outlet.",
        }),
      );
      return;
    }

    const exportFilters = {
      startDate: exportConfig.startDate,
      endDate: exportConfig.endDate,
      currency,
    };
    const trimmedSearch = String(activitySearch || "").trim();

    if (trimmedSearch) {
      exportFilters.search = trimmedSearch;
    }

    if (activityFilterType === "sold") {
      exportFilters.transactionType = "sold";
    } else if (activityFilterType === "return") {
      exportFilters.transactionType = "return";
    }

    if (selectedExportOutlet) {
      if (
        Number.isInteger(selectedExportOutlet.outletId) &&
        selectedExportOutlet.outletId > 0
      ) {
        exportFilters.outletId = selectedExportOutlet.outletId;
      } else if (selectedExportOutlet.locationTerm) {
        exportFilters.location = [selectedExportOutlet.locationTerm];
      }
    }

    try {
      setExportingCsv(true);
      setShowExportModal(false);
      const response = await exportMySalesCsv(exportFilters);

      const fallbackDate = exportConfig.endDate || toLocalDateInputValue(new Date());
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        resolveExportFilename(response.headers, fallbackDate),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      showSuccess(t("se.history.exportSuccess"));
    } catch (error) {
      console.error("Failed to export sales CSV:", error);

      let backendMessage = "";
      const errorPayload = error?.response?.data;
      if (errorPayload instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorPayload.text());
          backendMessage = parsed?.error || "";
        } catch (_) {
          backendMessage = "";
        }
      } else if (typeof errorPayload === "object" && errorPayload) {
        backendMessage = errorPayload.error || "";
      }

      showError(backendMessage || t("se.history.exportFailed"));
    } finally {
      setExportingCsv(false);
    }
  };

  const displayProofHistoryRecords = displayProofs.map((proof) => ({
    ...proof,
    __recordType: "display_proof",
    __recordKey: `display-proof-${proof.proof_id}`,
    __timestamp: proof.submitted_at || proof.created_at || null,
  }));

  const stockRequestHistoryRecords = stockRequests.map((request) => ({
    ...request,
    __recordType: "stock_request",
    __recordKey: `stock-request-${request.stock_request_id}`,
    __timestamp: request.requested_at || request.created_at || null,
  }));

  const getStockRequestItems = (record) =>
    Array.isArray(record.items) ? record.items : [];

  const getStockRequestLeadModel = (record) => {
    const first = getStockRequestItems(record)[0];
    if (!first) return t("se.history.noProducts");
    return (
      first.model_name ||
      first.product_name ||
      first.product_code ||
      t("se.history.noProducts")
    );
  };

  const getStockRequestUnitCount = (record) =>
    getStockRequestItems(record).reduce(
      (sum, item) => sum + (Number.parseInt(item.quantity, 10) || 0),
      0,
    );

  const visitHistoryRecords = checkIns
    .map((visit) => ({
      ...visit,
      __recordType: "check_in",
      __recordKey: `check-in-${visit.session_group_id || visit.check_in_id}`,
      __timestamp:
        visit.checked_in_at || visit.location_captured_at || visit.checked_out_at || null,
    }))
    .filter((record) => record.__timestamp);

  const historyRecords = [
    ...saleHistoryRecords,
    ...displayProofHistoryRecords,
    ...stockRequestHistoryRecords,
  ]
    .filter((record) => record.__timestamp)
    .sort(
      (a, b) =>
        new Date(b.__timestamp).getTime() - new Date(a.__timestamp).getTime(),
    );

  const mapRecordOutlet = (record) =>
    record.__recordType === "display_proof"
      ? record.outlet_name ||
        record.check_in?.outlet_name ||
        t("se.displayProof.unknownOutlet")
      : record.outlet?.bp_outlet_name ||
        record.checkIn?.outlet_name ||
        t("se.displayProof.unknownOutlet");

  const getSaleLeadItem = (record) =>
    Array.isArray(record.items) && record.items.length > 0 ? record.items[0] : null;

  const getSaleLeadModel = (record) => {
    const item = getSaleLeadItem(record);
    if (!item) return t("se.history.noProducts");

    return (
      item.product_model_code ||
      item.product_code ||
      item.product_name ||
      t("se.history.noProducts")
    );
  };

  const getSaleLeadColour = (record) => getSaleLeadItem(record)?.colour || null;

  const getSaleMetaSummary = (record) => {
    const outlet = mapRecordOutlet(record);
    const colour = getSaleLeadColour(record);
    return { outlet, colour };
  };

  const getDisplayProofSummary = (record) => {
    if (record.__recordType === "display_proof") {
      const items = Array.isArray(record.items) ? record.items : [];
      const priceRangeItems = items.filter(
        (item) => item.price_range_min != null || item.price_range_max != null,
      );
      const mins = priceRangeItems.map((item) => item.price_range_min).filter((v) => v != null);
      const maxs = priceRangeItems.map((item) => item.price_range_max).filter((v) => v != null);
      const priceRange = priceRangeItems.length
        ? {
            min: mins.length ? Math.min(...mins) : null,
            max: maxs.length ? Math.max(...maxs) : null,
            currency: priceRangeItems[0].price_range_currency || "MYR",
          }
        : null;

      return {
        proofTypeLabel: getProofTypeLabel(record.proof_type),
        modelNames: items.map((item) => item.product?.model_name).filter(Boolean),
        remarks: items.map((item) => item.notes).filter(Boolean).join(" | "),
        priceRange,
      };
    }

    return null;
  };

  const getVisitStatusMeta = (status) => {
    const code = normalizeVisitStatusCode(status);

    switch (code) {
      case "on_site":
        return {
          label: t("se.history.visitStatus.onSite"),
          tone: "success",
        };
      case "off_site":
        return {
          label: t("se.history.visitStatus.offSite"),
          tone: "danger",
        };
      default:
        return {
          label: t("se.history.visitStatus.unverified"),
          tone: "warning",
        };
    }
  };

  const formatVisitDistance = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return t("se.history.visitDistanceUnavailable");
    }

    if (numericValue >= 1000) {
      const km = numericValue / 1000;
      return `${km >= 10 ? km.toFixed(0) : km.toFixed(1)} km away`;
    }

    return `${Math.round(numericValue)} m away`;
  };

  const formatVisitTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return null;

    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const getAttendanceEventLabel = (type) => {
    switch (type) {
      case "clock_in":
        return t("se.history.attendance.clockIn");
      case "break_out":
        return t("se.history.attendance.breakOut");
      case "break_in":
        return t("se.history.attendance.breakIn");
      case "clock_out":
        return t("se.history.attendance.clockOut");
      default:
        return t("se.history.attendance.recordedAt");
    }
  };

  const getVisitRecordSessionGroupId = (record) =>
    Number.parseInt(record?.session_group_id || record?.check_in_id, 10) || null;

  const isRoadshowVisit = (record) => {
    if (!record) return false;
    const details = String(record.event_details || "").trim();
    if (record.is_event === true || record.is_event === 1) return true;
    const normalized = String(record.is_event || "").trim().toLowerCase();
    return (
      normalized === "true" ||
      normalized === "t" ||
      normalized === "1" ||
      details.toLowerCase() === "roadshow"
    );
  };

  const shouldShowRoadshowDetails = (details) => {
    const normalizedDetails = String(details || "").trim();
    if (!normalizedDetails) return false;
    return normalizedDetails.toLowerCase() !== "roadshow";
  };

  const getVisitDetailsLabel = (record) => {
    if (!record) return t("admin.sessions.outletCheckIn");
    return isRoadshowVisit(record)
      ? t("se.history.roadshowLabel")
      : t("admin.sessions.outletCheckIn");
  };

  const mapRecordItemSummary = (record) => {
    if (record.__recordType === "display_proof") {
      return getDisplayProofSummary(record)?.proofTypeLabel || null;
    }

    if ((record.items?.length || 0) > 1) {
      return t("se.history.additionalItems", {
        defaultValue: "+{{count}} more item(s)",
        count: (record.items?.length || 1) - 1,
      });
    }

    return null;
  };

  const filteredRecords = historyRecords.filter((record) => {
    const recordDate = new Date(record.__timestamp).toISOString().split("T")[0];
    const matchDate = activityFilterDate ? recordDate === activityFilterDate : true;

    const outletName = mapRecordOutlet(record);

    const typeMatch =
      activityFilterType === "All"
        ? true
        : activityFilterType === "sold"
          ? record.__recordType === "sale" && Number(record.total_amount || 0) >= 0
          : activityFilterType === "return"
            ? record.__recordType === "sale" && Number(record.total_amount || 0) < 0
            : activityFilterType === "stock_request"
              ? record.__recordType === "stock_request"
              : record.__recordType === "display_proof";

    if (record.__recordType === "stock_request") {
      const srItems = getStockRequestItems(record);
      const srSearch = [
        outletName,
        record.order_no,
        ...srItems.map((item) => item.model_name),
        ...srItems.map((item) => item.product_name),
        ...srItems.map((item) => item.product_code),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchSearch = activitySearch.trim()
        ? srSearch.includes(activitySearch.trim().toLowerCase())
        : true;
      return matchDate && typeMatch && matchSearch;
    }

    if (record.__recordType === "display_proof") {
      const proofItems = Array.isArray(record.items) ? record.items : [];
      const proofSearch = [
        outletName,
        getProofTypeLabel(record.proof_type),
        ...proofItems.map((item) => item.notes),
        ...proofItems.map((item) => item.product?.model_name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchSearch = activitySearch.trim()
        ? proofSearch.includes(activitySearch.trim().toLowerCase())
        : true;
      return matchDate && typeMatch && matchSearch;
    }

    const saleSearch = [
      outletName,
      ...((record.items || []).flatMap((item) => [
        item.product_model_code,
        item.product_model_description,
        item.product_code,
        item.product_name,
        item.colour,
      ])),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchSearch = activitySearch.trim()
      ? saleSearch.includes(activitySearch.trim().toLowerCase())
      : true;

    return matchDate && typeMatch && matchSearch;
  });

  const filteredVisitRecords = visitHistoryRecords.filter((record) => {
    const statusLabel = getVisitStatusMeta(record.location_match_status).label;
    const distanceLabel = formatVisitDistance(record.distance_from_outlet_meters);
    const roadshowActive = isRoadshowVisit(record);
    const visitSearchTerms = [
      record.outlet_name,
      record.outlet_address,
      statusLabel,
      distanceLabel,
      ...(Array.isArray(record.attendance_events)
        ? record.attendance_events.flatMap((event) => [
            getAttendanceEventLabel(event?.type),
            formatVisitTimestamp(event?.at)?.time || "",
          ])
        : []),
      roadshowActive ? t("se.history.roadshowLabel") : "",
      record.event_details,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchSearch = visitSearch.trim()
      ? visitSearchTerms.includes(visitSearch.trim().toLowerCase())
      : true;

    const normalizedStatusFilter = String(visitFilterEventStatus || "All").toLowerCase();
    const visitStatusValue = roadshowActive ? "roadshow" : "outlet_check_in";
    const matchStatus =
      normalizedStatusFilter === "all" ? true : visitStatusValue === normalizedStatusFilter;

    return matchSearch && matchStatus;
  });

  const hasVisitRecords = filteredVisitRecords.length > 0;
  const hasHistoryRecords = filteredRecords.length > 0;

  return (
    <div className="history-view">
      <div className="history-search-bar">
        <Search size={18} className="history-search-bar-icon" />
        <input
          type="text"
          value={activeSearchValue}
          onChange={(e) => {
            const value = e.target.value;
            if (isVisitsTab) {
              setVisitSearch(value);
              return;
            }

            setActivitySearch(value);
          }}
          placeholder={activeSearchPlaceholder}
          className="history-search-bar-input"
        />
      </div>

      <div className="history-tabs" role="tablist" aria-label={t("se.history.historyTabsLabel")}>
        <button
          type="button"
          role="tab"
          aria-selected={isVisitsTab}
          className={`history-tab${isVisitsTab ? " is-active" : ""}`}
          onClick={() => setActiveHistoryTab("visits")}
        >
          {t("se.history.tabs.visits")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isVisitsTab}
          className={`history-tab${!isVisitsTab ? " is-active" : ""}`}
          onClick={() => setActiveHistoryTab("activity")}
        >
          {t("se.history.tabs.activity")}
        </button>
      </div>

      <div className="filter-container">
        <div
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="filter-header"
        >
          <div className="filter-title">
            <Filter size={16} /> {t("se.history.filter")}
          </div>
          <span className="filter-toggle">
            {isFilterOpen ? t("se.history.hide") : t("se.history.show")}
          </span>
        </div>

        {isFilterOpen && (
          <div className="filter-body">
            <div className="filter-row">
              <div
                className={`filter-field${isVisitsTab ? " filter-field--full" : ""}`}
              >
                <label>{t("se.history.date")}</label>
                <input
                  type="date"
                  className="dropdown-input filter-date-input"
                  value={isVisitsTab ? visitFilterDate : activityFilterDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isVisitsTab) {
                      setVisitFilterDate(value);
                      return;
                    }

                    setActivityFilterDate(value);
                  }}
                />
                </div>

              {isVisitsTab ? (
                <div className="filter-field">
                  <label>{t("se.history.eventStatus", { defaultValue: "Event Status" })}</label>
                  <select
                    className="dropdown-input"
                    value={visitFilterEventStatus}
                    onChange={(e) => setVisitFilterEventStatus(e.target.value)}
                  >
                    <option value="All">{t("se.history.allTypes")}</option>
                    <option value="roadshow">{t("se.history.roadshowLabel")}</option>
                    <option value="outlet_check_in">
                      {t("admin.sessions.outletCheckIn")}
                    </option>
                  </select>
                </div>
              ) : null}

              {!isVisitsTab ? (
                <div className="filter-field">
                  <label>{t("se.history.type")}</label>
                  <select
                    className="dropdown-input"
                    value={activityFilterType}
                    onChange={(e) => setActivityFilterType(e.target.value)}
                  >
                    <option value="All">{t("se.history.allTypes")}</option>
                    <option value="sold">{t("se.history.soldLabel")}</option>
                    <option value="return">{t("se.history.returnLabel")}</option>
                    <option value="display_proof">{t("se.history.displayProofLabel")}</option>
                    <option value="stock_request">
                      {t("se.history.stockRequestLabel", {
                        defaultValue: "Stock Request",
                      })}
                    </option>
                  </select>
                </div>
              ) : null}
            </div>

            <button
              onClick={() => {
                resetActiveFilters();
              }}
              className="filter-clear"
            >
              {t("se.history.clearFilters")}
            </button>
          </div>
        )}
      </div>

      {loadingHistory ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Loader2 size={24} className="history-loading-icon" />
          </div>
          <p>{t("se.history.loading")}</p>
        </div>
      ) : (
        <>
          {isVisitsTab ? (
            hasVisitRecords ? (
              <>
                <section className="history-visit-section">
                  <div className="history-visit-section-header">
                    <div>
                      <h3>{t("se.history.recentCheckIns")}</h3>
                      <p>{t("se.history.recentCheckInsSubtitle")}</p>
                    </div>
                  </div>

                  <div className="history-visit-list">
                    {filteredVisitRecords.map((record) => {
                      const statusMeta = getVisitStatusMeta(record.location_match_status);
                      const timestamp = formatVisitTimestamp(record.__timestamp);
                      const distanceLabel = formatVisitDistance(record.distance_from_outlet_meters);
                      const roadshowActive = isRoadshowVisit(record);
                      const roadshowDetails = String(record.event_details || "").trim();
                      const statusClass = `is-${statusMeta.tone}`;
                    return (
                      <article
                        key={record.__recordKey}
                        className={`history-visit-card ${statusClass}`}
                        onClick={() =>
                          navigate(`/visit-details/${getVisitRecordSessionGroupId(record)}`, {
                            state: {
                              visitData: record,
                              previousView: "HISTORY",
                            },
                          })
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <div className="history-visit-stripe" aria-hidden="true" />
                        <div className="history-visit-content">
                          <div className="history-visit-header">
                            <div className="history-date history-visit-date">
                              <Calendar size={12} />
                              {timestamp
                                ? timestamp.date
                                : t("se.history.visitCheckedInFallback")}
                              {timestamp ? (
                                <span className="history-date-separator" aria-hidden="true">
                                  |
                                </span>
                              ) : null}
                              {timestamp ? (
                                <span className="history-time">{timestamp.time}</span>
                              ) : null}
                            </div>

                            <div className="history-visit-header-actions">
                              <span className={`history-visit-status-pill ${statusClass}`}>
                                {statusMeta.label}
                              </span>
                              <span className="history-view-arrow" aria-hidden="true">
                                ›
                              </span>
                            </div>
                          </div>

                          <div className="history-visit-row">
                            <div className="history-visit-copy">
                              <div className="history-visit-outlet">
                                {record.outlet_name ||
                                  record.outlet?.bp_outlet_name ||
                                  t("se.displayProof.unknownOutlet")}
                              </div>
                                <div className="history-visit-address">
                                  {record.outlet_address ||
                                    record.outlet?.address ||
                                    t("se.history.visitNoAddress")}
                                </div>
                                {roadshowActive ? (
                                  <div className="history-visit-event">
                                    <span className="history-visit-event-pill is-roadshow">
                                      {getVisitDetailsLabel(record)}
                                    </span>
                                    {shouldShowRoadshowDetails(roadshowDetails) ? (
                                      <span className="history-visit-event-details">
                                        {roadshowDetails}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className="history-visit-event">
                                    <span className="history-visit-event-pill is-outlet">
                                      {t("admin.sessions.outletCheckIn")}
                                    </span>
                                  </div>
                                )}
                              </div>

                            <div className="history-visit-distance">{distanceLabel}</div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                  </div>
                </section>
                <div className="history-footer">
                  {t("se.history.showingCheckIns", { count: filteredVisitRecords.length })}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <Search size={24} />
                </div>
                <p>{t("se.history.noVisitRecordsMatch")}</p>
              </div>
            )
          ) : (
            <>
              <div className="history-activity-section-header">
                <div className="history-activity-section-heading">
                  <h3>{t("se.history.activityHistory")}</h3>
                  <p>{t("se.history.activityHistorySubtitle")}</p>
                </div>
                <button
                  type="button"
                  className="history-export-btn history-export-btn-inline"
                  onClick={openExportModal}
                  disabled={loadingHistory || exportingCsv}
                >
                  <Download size={16} />
                  {exportingCsv ? t("se.history.exportingCsv") : t("se.history.exportCsv")}
                </button>
              </div>

              {hasHistoryRecords ? (
                <>
              {filteredRecords.map((record) => {
                const isDisplayProof = record.__recordType === "display_proof";
                const isSale = record.__recordType === "sale";
                const isStockRequest = record.__recordType === "stock_request";
                const isReturnSale = isSale && Number(record.total_amount || 0) < 0;
                const recordDate = new Date(record.__timestamp);
                const formattedDate = recordDate.toLocaleDateString();
                const formattedTime = recordDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });

                const cardProps = isSale
                  ? {
                      onClick: () =>
                        navigate("/sale-details", {
                          state: {
                            saleData: record,
                            previousView: "HISTORY",
                          },
                        }),
                      style: { cursor: "pointer" },
                    }
                  : isDisplayProof
                    ? {
                        onClick: () =>
                          navigate("/display-proof-details", {
                            state: {
                              proofData: record,
                              previousView: "HISTORY",
                            },
                          }),
                        style: { cursor: "pointer" },
                      }
                    : isStockRequest
                      ? {
                          onClick: () =>
                            navigate("/stock-request-details", {
                              state: {
                                requestData: record,
                                previousView: "HISTORY",
                              },
                            }),
                          style: { cursor: "pointer" },
                        }
                      : {};

                const saleMeta = isSale ? getSaleMetaSummary(record) : null;
                const extraSummary = mapRecordItemSummary(record);
                const displayProofSummary = isDisplayProof
                  ? getDisplayProofSummary(record)
                  : null;
                const saleColourPresentation = saleMeta?.colour
                  ? getColourPresentation(saleMeta.colour)
                  : null;

                return (
                  <div key={record.__recordKey} className="history-card" {...cardProps}>
                    <div className="history-stripe"></div>
                    <div className="history-header">
                      <div className="history-date">
                        <Calendar size={12} />
                        {formattedDate}
                        <span className="history-date-separator" aria-hidden="true">
                          |
                        </span>
                        <span className="history-time">{formattedTime}</span>
                      </div>
                      <div className="history-header-actions">
                        {isStockRequest ? (
                          <>
                            <span className="history-proof-badge">
                              <PackagePlus size={14} className="history-badge-icon" />
                              {t("se.history.stockRequestLabel", {
                                defaultValue: "Stock Request",
                              })}
                            </span>
                            <span className={`sr-history-badge is-${record.status}`}>
                              {t(`se.stockRequest.history.status.${record.status}`, {
                                defaultValue: record.status,
                              })}
                            </span>
                          </>
                        ) : isDisplayProof ? (
                          <span className="history-proof-badge">
                            <ImageIcon size={14} className="history-badge-icon" />
                            {t("se.history.displayProofLabel")}
                          </span>
                        ) : isReturnSale ? (
                          <span className="history-return-badge">
                            {t("se.history.returnLabel")}
                          </span>
                        ) : (
                          <span className="history-sale-badge">
                            <CheckCircle2 size={14} className="history-badge-icon" />
                            {t("se.history.soldLabel")}
                          </span>
                        )}
                        <span className="history-view-arrow">›</span>
                      </div>
                    </div>

                    <div className="history-body">
                      <div className="history-details">
                        {isStockRequest ? (
                          <>
                            <div className="history-model">
                              {getStockRequestLeadModel(record)}
                            </div>
                            <div className="history-outlet">
                              {mapRecordOutlet(record)}
                            </div>
                            {record.order_no ? (
                              <div className="history-item">{record.order_no}</div>
                            ) : null}
                            {getStockRequestItems(record).length > 1 ? (
                              <div className="history-item">
                                {t("se.history.additionalItems", {
                                  defaultValue: "+{{count}} more item(s)",
                                  count: getStockRequestItems(record).length - 1,
                                })}
                              </div>
                            ) : null}
                          </>
                        ) : isSale ? (
                          <>
                            <div className="history-model">{getSaleLeadModel(record)}</div>
                            <div className="history-outlet">{saleMeta?.outlet}</div>
                            {saleMeta?.colour ? (
                              <div className="history-colour">
                                <span className="history-colour-label">
                                  {t("se.saleForm.colourHelperLabel")}
                                </span>
                                <span
                                  className="history-colour-value"
                                  style={saleColourPresentation?.badgeStyle}
                                >
                                  <span
                                    className="history-colour-swatch"
                                    style={saleColourPresentation?.swatchStyle}
                                    aria-hidden="true"
                                  />
                                  {saleMeta.colour}
                                </span>
                              </div>
                            ) : null}
                            {extraSummary ? <div className="history-item">{extraSummary}</div> : null}
                          </>
                        ) : (
                          <>
                            <div className="history-model">{mapRecordOutlet(record)}</div>
                            {displayProofSummary?.modelNames?.map((modelName, modelIndex) => (
                              <div
                                key={`${record.__recordKey}-model-${modelIndex}`}
                                className="history-proof-model-pill"
                              >
                                {modelName}
                              </div>
                            ))}
                            {displayProofSummary?.proofTypeLabel ? (
                              <div className="history-proof-type-pill">
                                {displayProofSummary.proofTypeLabel}
                              </div>
                            ) : null}
                            {displayProofSummary?.remarks ? (
                              <div className="history-proof-description">
                                <span className="history-proof-description-label">
                                  {t("se.history.notesLabel", {
                                    defaultValue: "Notes:",
                                  })}
                                </span>{" "}
                                <span className="history-proof-description-text">
                                  {displayProofSummary.remarks}
                                </span>
                              </div>
                            ) : null}
                            {displayProofSummary?.priceRange ? (
                              <div className="history-proof-description">
                                <span className="history-proof-description-label">
                                  {t("se.history.priceRangeLabel", {
                                    defaultValue: "Price Range:",
                                  })}
                                </span>{" "}
                                <span className="history-proof-description-text">
                                  {displayProofSummary.priceRange.min != null &&
                                  displayProofSummary.priceRange.max != null &&
                                  displayProofSummary.priceRange.min !==
                                    displayProofSummary.priceRange.max
                                    ? `${formatCurrency(
                                        displayProofSummary.priceRange.min,
                                        record.__timestamp,
                                        displayProofSummary.priceRange.currency,
                                      )} - ${formatCurrency(
                                        displayProofSummary.priceRange.max,
                                        record.__timestamp,
                                        displayProofSummary.priceRange.currency,
                                      )}`
                                    : formatCurrency(
                                        displayProofSummary.priceRange.min ??
                                          displayProofSummary.priceRange.max,
                                        record.__timestamp,
                                        displayProofSummary.priceRange.currency,
                                      )}
                                </span>
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                      {isSale ? (
                        <div className={`history-total${isReturnSale ? " history-total--negative" : ""}`}>
                          <span>
                            {formatCurrency(
                              Number(record.total_amount || 0),
                              record.__timestamp,
                              record.currency,
                            )}
                          </span>
                        </div>
                      ) : isStockRequest ? (
                        <div className="history-total history-total--meta">
                          <span>
                            {t("se.stockRequest.history.unitCount", {
                              count: getStockRequestUnitCount(record),
                            })}
                          </span>
                        </div>
                      ) : (
                        <div className="history-total history-total--meta">
                          <span>
                            {t("se.history.proofImageCount", {
                              count: Number(record.image_count || 0),
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="history-footer">
                {t("se.history.showingRecords", { count: filteredRecords.length })}
              </div>
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <Search size={24} />
                  </div>
                  <p>{t("se.history.noRecordsMatch")}</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {showExportModal ? (
        <div
          className="history-export-modal-backdrop"
          role="presentation"
          onClick={closeExportModal}
        >
          <div
            className="history-export-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="history-export-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="history-export-modal-header">
              <div className="history-export-modal-header-copy">
                <h3 id="history-export-modal-title" className="history-export-modal-title">
                  {t("se.history.exportModal.title", {
                    defaultValue: "Export Sales CSV",
                  })}
                </h3>
                <p className="history-export-modal-subtitle">
                  {t("se.history.exportModal.subtitle", {
                    defaultValue: "Choose outlet scope and date range before exporting.",
                  })}
                </p>
              </div>
              <button
                type="button"
                className="history-export-modal-close"
                onClick={closeExportModal}
                aria-label={t("se.history.exportModal.close", {
                  defaultValue: "Close",
                })}
              >
                <X size={16} />
              </button>
            </div>

            <div className="history-export-modal-field">
              <label>{t("se.history.exportModal.quickRangeLabel", { defaultValue: "Quick range" })}</label>
              <div className="history-export-modal-presets">
                {exportDatePresetOptions.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`history-export-modal-preset-btn${
                      exportConfig.datePreset === preset.value ? " is-active" : ""
                    }`}
                    onClick={() => applyExportDatePreset(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <p className="history-export-modal-range-preview">
                {t("se.history.exportModal.dateRangePreview", {
                  defaultValue: "From {{start}} to {{end}}",
                  start: exportConfig.startDate || "-",
                  end: exportConfig.endDate || "-",
                })}
              </p>
            </div>

            {exportConfig.datePreset === "custom" ? (
              <div className="history-export-modal-date-grid">
                <div className="history-export-modal-field">
                  <label>{t("se.history.exportModal.startDate", { defaultValue: "Start date" })}</label>
                  <input
                    type="date"
                    className="history-export-modal-input"
                    value={exportConfig.startDate}
                    onChange={(event) =>
                      setExportConfig((previous) => ({
                        ...previous,
                        datePreset: "custom",
                        startDate: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="history-export-modal-field">
                  <label>{t("se.history.exportModal.endDate", { defaultValue: "End date" })}</label>
                  <input
                    type="date"
                    className="history-export-modal-input"
                    value={exportConfig.endDate}
                    onChange={(event) =>
                      setExportConfig((previous) => ({
                        ...previous,
                        datePreset: "custom",
                        endDate: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            ) : null}

            <div className="history-export-modal-field">
              <label>{t("se.history.exportModal.outletScopeLabel", { defaultValue: "Outlet" })}</label>
              <div className="history-export-modal-scope">
                <button
                  type="button"
                  className={`history-export-modal-scope-option${
                    exportConfig.outletScope === "all" ? " is-active" : ""
                  }`}
                  onClick={() =>
                    setExportConfig((previous) => ({
                      ...previous,
                      outletScope: "all",
                      outletKey: "",
                    }))
                  }
                >
                  {t("se.history.exportModal.allOutlets", { defaultValue: "All outlets" })}
                </button>
                <button
                  type="button"
                  className={`history-export-modal-scope-option${
                    exportConfig.outletScope === "single" ? " is-active" : ""
                  }`}
                  onClick={() =>
                    setExportConfig((previous) => ({
                      ...previous,
                      outletScope: "single",
                      outletKey: previous.outletKey || exportOutletOptions[0]?.key || "",
                    }))
                  }
                >
                  {t("se.history.exportModal.singleOutlet", { defaultValue: "One outlet" })}
                </button>
              </div>
            </div>

            {exportConfig.outletScope === "single" ? (
              <div className="history-export-modal-field">
                <label>{t("se.history.exportModal.selectOutletLabel", { defaultValue: "Select outlet" })}</label>
                <select
                  className="history-export-modal-select"
                  value={exportConfig.outletKey}
                  disabled={exportOutletOptions.length === 0}
                  onChange={(event) =>
                    setExportConfig((previous) => ({
                      ...previous,
                      outletKey: event.target.value,
                    }))
                  }
                >
                  <option value="">
                    {t("se.history.exportModal.selectOutletPlaceholder", {
                      defaultValue: "Choose an outlet",
                    })}
                  </option>
                  {exportOutletOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {exportOutletOptions.length === 0 ? (
                  <p className="history-export-modal-inline-note">
                    {t("se.history.exportModal.noOutletOptions", {
                      defaultValue: "No outlet history found.",
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}

            {exportConfig.datePreset !== "custom" ? (
              <p className="history-export-modal-inline-note">
                {t("se.history.exportModal.customDateHint", {
                  defaultValue: "Need a specific range? Select Custom.",
                })}
              </p>
            ) : null}

            <div className="history-export-modal-actions">
              <button
                type="button"
                className="history-export-modal-cancel"
                onClick={closeExportModal}
                disabled={exportingCsv}
              >
                {t("se.history.exportModal.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                className="history-export-modal-confirm"
                onClick={handleConfirmExport}
                disabled={
                  exportingCsv ||
                  !exportConfig.startDate ||
                  !exportConfig.endDate ||
                  exportConfig.endDate < exportConfig.startDate ||
                  (exportConfig.outletScope === "single" && !selectedExportOutlet)
                }
              >
                {exportingCsv
                  ? t("se.history.exportingCsv")
                  : t("se.history.exportModal.confirm", { defaultValue: "Export CSV" })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default HistoryView;
